(function (window, document) {
  'use strict';

  if (window.codexUSPromotionCalendar) {
    window.codexUSPromotionCalendar.refresh();
    return;
  }

  const rootSelector = '[data-promotion-root]';
  const calendarSelector = '[data-promo-calendar]';
  const announcementSelector = '[data-promo-original-announcement]';
  const roots = new Map();
  const originalAnnouncements = new Map();
  const unloadedRoots = new WeakSet();
  let timer = null;
  let observerRefreshQueued = false;

  function getCode(campaign) {
    return campaign && typeof campaign.code === 'string' ? campaign.code.trim() : '';
  }

  function parseWindow(campaign) {
    if (!getCode(campaign)) return null;
    const hasOffset = /T.*(?:Z|[+-]\d{2}:\d{2})$/;
    if (typeof campaign.startsAt !== 'string' || typeof campaign.endsAt !== 'string') return null;
    if (!hasOffset.test(campaign.startsAt) || !hasOffset.test(campaign.endsAt)) return null;
    const start = Date.parse(campaign.startsAt);
    const end = Date.parse(campaign.endsAt);
    return Number.isFinite(start) && Number.isFinite(end) && start < end ? { start, end } : null;
  }

  function selectCampaign(campaigns, now) {
    if (!Array.isArray(campaigns) || !Number.isFinite(now)) return null;
    for (const campaign of campaigns) {
      const range = parseWindow(campaign);
      if (range && range.start <= now && now < range.end) return campaign;
    }
    return null;
  }

  function setText(root, selector, value) {
    const text = typeof value === 'string' ? value : '';
    root.querySelectorAll(selector).forEach(function (slot) {
      if (slot.textContent !== text) slot.textContent = text;
    });
  }

  function getLabel(labels, keys) {
    for (const key of keys) {
      if (typeof labels[key] === 'string') return labels[key];
    }
    return '';
  }

  function formatDeadline(campaign, labels) {
    const start = new Date((campaign.startDate || campaign.startsAt.slice(0, 10)) + 'T12:00:00Z');
    const end = new Date(campaign.endDate + 'T12:00:00Z');
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return '';
    const formatter = new Intl.DateTimeFormat('en-US', {month:'short',day:'numeric',year:'numeric',timeZone:'UTC'});
    const dates = formatter.formatRange ? formatter.formatRange(start, end) : formatter.format(start) + ' – ' + formatter.format(end);
    return dates + ' · ' + getLabel(labels, ['timeZoneLabel', 'timeZone']);
  }

  function getShopPath(campaign) {
    if (window.location && /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:products|collections)\//.test(window.location.pathname)) {
      const params = new URLSearchParams(window.location.search);
      params.delete('promo_preview'); params.delete('preview_theme_id');
      return window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    }
    const path = typeof campaign.shopPath === 'string' ? campaign.shopPath : '';
    return path.startsWith('/') && !path.startsWith('//') && !/[\\\r\n]/.test(path)
      ? path : '/collections/all';
  }

  function prepareRoot(root) {
    const calendar = Array.from(root.querySelectorAll(calendarSelector)).find(function (script) {
      return script.closest(rootSelector) === root;
    });
    const text = calendar ? calendar.textContent : '';
    const previous = roots.get(root);
    if (previous && previous.calendarText === text) return previous;
    let data = null;
    try {
      data = JSON.parse(text);
    } catch (error) {
      data = null;
    }
    const state = {
      root,
      calendarText: text,
      campaigns: data && Array.isArray(data.campaigns) ? data.campaigns : [],
      labels: data && data.labels && typeof data.labels === 'object' ? data.labels : {},
      previewCode: root.dataset.previewAllowed === 'true' && window.location ? new URLSearchParams(window.location.search).get('promo_preview') : null,
      active: null
    };
    roots.set(root, state);
    root.querySelectorAll('[data-promo-feedback]').forEach(function (slot) {
      slot.setAttribute('role', 'status');
      slot.setAttribute('aria-live', 'polite');
      slot.setAttribute('aria-atomic', 'true');
    });
    root.querySelectorAll('[data-promo-copy]').forEach(function (button) {
      if (button.tagName === 'BUTTON') button.setAttribute('type', 'button');
    });
    return state;
  }

  function render(state, campaign) {
    if (state.active !== campaign) setText(state.root, '[data-promo-feedback]', '');
    state.active = campaign;
    if (!campaign) {
      state.root.hidden = true;
      return;
    }
    setText(state.root, '[data-promo-title]', campaign.title);
    setText(state.root, '[data-promo-description]', campaign.description);
    setText(state.root, '[data-promo-code]', getCode(campaign));
    setText(state.root, '[data-promo-deadline]', formatDeadline(campaign, state.labels));
    state.root.querySelectorAll('[data-promo-preview-notice]').forEach(function (notice) { notice.hidden = !state.previewCode; });
    const href = '/discount/' + encodeURIComponent(getCode(campaign)) +
      '?redirect=' + encodeURIComponent(getShopPath(campaign));
    state.root.querySelectorAll('[data-promo-link]').forEach(function (link) {
      const destination = state.previewCode ? '#' : href;
      if (link.getAttribute('href') !== destination) link.setAttribute('href', destination);
      if (state.previewCode) link.setAttribute('aria-disabled','true');
      else link.removeAttribute('aria-disabled');
    });
    state.root.hidden = false;
  }

  function syncOriginalAnnouncements() {
    const activeBar = Array.from(roots.values()).some(function (state) {
      return state.active && !state.root.hidden && state.root.dataset.mode === 'bar';
    });
    originalAnnouncements.forEach(function (hidden, element) {
      if (!element.isConnected) {
        element.hidden = hidden;
        originalAnnouncements.delete(element);
      }
    });
    document.querySelectorAll(announcementSelector).forEach(function (element) {
      if (activeBar) {
        if (!originalAnnouncements.has(element)) originalAnnouncements.set(element, element.hidden);
        element.hidden = true;
      } else if (originalAnnouncements.has(element)) {
        element.hidden = originalAnnouncements.get(element);
        originalAnnouncements.delete(element);
      }
    });
  }

  function schedule(now) {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
    if (!roots.size) return;
    let delay = 60000;
    roots.forEach(function (state) {
      state.campaigns.forEach(function (campaign) {
        const range = parseWindow(campaign);
        if (!range) return;
        if (range.start > now) delay = Math.min(delay, range.start - now);
        if (range.end > now) delay = Math.min(delay, range.end - now);
      });
    });
    timer = window.setTimeout(refresh, Math.max(1000, Math.min(60000, delay)));
  }

  function refresh() {
    const now = Date.now();
    roots.forEach(function (state, root) {
      if (!root.isConnected || unloadedRoots.has(root)) roots.delete(root);
    });
    document.querySelectorAll(rootSelector).forEach(function (root) {
      if (unloadedRoots.has(root)) return;
      const state = prepareRoot(root);
      render(state, state.previewCode ? state.campaigns.find(function (c) { return getCode(c).toLowerCase() === state.previewCode.toLowerCase(); }) : selectCampaign(state.campaigns, now));
    });
    syncOriginalAnnouncements();
    schedule(now);
  }

  function rootsWithin(element) {
    if (!element || typeof element.querySelectorAll !== 'function') return [];
    const found = Array.from(element.querySelectorAll(rootSelector));
    if (typeof element.matches === 'function' && element.matches(rootSelector)) found.unshift(element);
    return found;
  }

  function loadSection(event) {
    rootsWithin(event.target).forEach(function (root) { unloadedRoots.delete(root); });
    refresh();
  }

  function unloadSection(event) {
    rootsWithin(event.target).forEach(function (root) {
      unloadedRoots.add(root);
      root.hidden = true;
      roots.delete(root);
    });
    syncOriginalAnnouncements();
    schedule(Date.now());
  }

  async function copyCode(event) {
    const target = event.target && typeof event.target.closest === 'function'
      ? event.target.closest('[data-promo-copy]') : null;
    if (!target) return;
    const root = target.closest(rootSelector);
    if (!root || !roots.has(root)) return;
    event.preventDefault();
    refresh();
    const state = roots.get(root);
    const campaign = state && state.active;
    if (!campaign || root.hidden) return;
    const code = getCode(campaign);
    let success = false;
    try {
      if (!window.navigator.clipboard || typeof window.navigator.clipboard.writeText !== 'function') {
        throw new Error('Clipboard unavailable');
      }
      await window.navigator.clipboard.writeText(code);
      success = true;
    } catch (error) {
      success = false;
    }
    refresh();
    if (roots.get(root) !== state || state.active !== campaign || root.hidden) return;
    setText(root, '[data-promo-feedback]', success
      ? getLabel(state.labels, ['copySuccess', 'copied'])
      : getLabel(state.labels, ['copyFailure', 'copyError']));
  }

  function relevantNode(node) {
    if (!node || node.nodeType !== 1) return false;
    const selector = rootSelector + ',' + calendarSelector + ',' + announcementSelector;
    return node.matches(selector) || Boolean(node.querySelector(selector));
  }

  if (typeof window.MutationObserver === 'function') {
    const observer = new window.MutationObserver(function (records) {
      const relevant = records.some(function (record) {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        return target && target.matches(calendarSelector) ||
          Array.from(record.addedNodes).some(relevantNode) ||
          Array.from(record.removedNodes).some(relevantNode);
      });
      if (!relevant || observerRefreshQueued) return;
      observerRefreshQueued = true;
      Promise.resolve().then(function () {
        observerRefreshQueued = false;
        refresh();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  }

  document.addEventListener('click', copyCode);
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('shopify:section:load', loadSection);
  document.addEventListener('shopify:section:unload', unloadSection);
  window.addEventListener('pageshow', refresh);
  window.codexUSPromotionCalendar = { version: '2.0.0', selectCampaign, refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})(window, document);
